/**
 * 사용자 권한 상수 정의
 */

export const ROLES = {
  ADMIN: 'admin',
  USER: 'user',
  PREMIUM: 'Premium',
  GYEONGGI_PHARMACIST: '경기도약사회',
  SEOUL_PHARMACIST: '서울시약사회',
  INCHEON_PHARMACIST: '인천시약사회'
} as const;

export type RoleType = typeof ROLES[keyof typeof ROLES];

/**
 * Premium 사용자로 분류되는 권한 목록
 * 이 권한을 가진 사용자는 포인트 차감 없이 서비스 이용 가능
 */
export const PREMIUM_ROLES: readonly string[] = [
  ROLES.ADMIN,
  ROLES.PREMIUM,
  ROLES.GYEONGGI_PHARMACIST,
  ROLES.SEOUL_PHARMACIST,
  ROLES.INCHEON_PHARMACIST
] as const;

/**
 * 권한별 한글명
 */
export const ROLE_NAMES: Record<string, string> = {
  [ROLES.ADMIN]: '관리자',
  [ROLES.USER]: '일반 사용자',
  [ROLES.PREMIUM]: 'Premium',
  [ROLES.GYEONGGI_PHARMACIST]: '경기도약사회',
  [ROLES.SEOUL_PHARMACIST]: '서울시약사회',
  [ROLES.INCHEON_PHARMACIST]: '인천시약사회'
};

/**
 * Premium 사용자 여부 확인 헬퍼 함수
 * "서울시약사회/강남구"와 같은 계층적 역할도 지원 (부분 일치)
 *
 * 예시:
 * - "서울시약사회" → true (정확히 일치)
 * - "서울시약사회/강남구" → true (서울시약사회로 시작)
 * - "경기도약사회/수원시" → true (경기도약사회로 시작)
 * - "admin" → true (정확히 일치)
 * - "user" → false (PREMIUM_ROLES에 없음)
 */
export const isPremiumRole = (role: string): boolean => {
  // 정확히 일치하는 경우
  if (PREMIUM_ROLES.includes(role)) {
    return true;
  }

  // 부분 일치 체크: "서울시약사회/강남구"가 "서울시약사회"로 시작하는지 확인
  return PREMIUM_ROLES.some(premiumRole =>
    role.startsWith(premiumRole + '/')
  );
};

/**
 * 사용자의 권한 배열에서 Premium 권한 보유 여부 확인
 */
export const hasAnyPremiumRole = (roles?: string[]): boolean => {
  if (!roles || roles.length === 0) return false;
  return roles.some(role => isPremiumRole(role));
};

/**
 * 사용자의 권한 배열에서 프리미엄 권한 추출
 * Separator 방식 지원 (예: "서울시약사회/구로구")
 *
 * @param roles 사용자 권한 배열
 * @returns 프리미엄 권한 (없으면 null)
 */
export const getPremiumRole = (roles?: string[]): string | null => {
  if (!roles || roles.length === 0) return null;

  // 프리미엄 권한 찾기 (조직명 또는 "조직명/지역명" 형태)
  return roles.find(role => isPremiumRole(role)) || null;
};